import { createContext, useContext } from 'react';

export type DisplayEnv = {
  /** "נכון ל-…" כפי שמוצג על ה-Screen — נצרב גם לייצוא. */
  asOfText: string;
  exportedBy?: string;
  /** הורדת קובץ (FileBlock) — GET /api/files/:id עם token; מסופק על ידי האפליקציה. */
  fetchFile?: (id: string) => Promise<Blob>;
  /** רינדור ל-PDF (09 §4): כל השורות, בלי כפתורים. */
  print?: boolean;
  /**
   * web-screen-budget: בלוק בתוך שיחה (ובתצוגה של aibify-preview לבלוק בודד) — טבלה מציגה עד 12 שורות ו"עוד N שורות"
   * שפותח הגדלה (שם — כל השורות). מסך יצירה וריצה שמורה — בלי (שם הטבלה היא התוכן).
   */
  compact?: boolean;
};

export const DisplayContext = createContext<DisplayEnv>({ asOfText: '' });

/**
 * web-screen-budget: מחסנית השכבות של האפליקציה (Esc סוגר רק את העליונה) — ההגדלה נרשמת בה כשהיא פתוחה. push מחזיר
 * הסרה. בלי מחסנית (aibify-preview, דף עצמאי) — ההגדלה מאזינה ל-Esc בעצמה.
 */
export type LayerStack = { push(close: () => void): () => void };
export const LayerContext = createContext<LayerStack | null>(null);
export const useDisplay = () => useContext(DisplayContext);

/** 16: תוצאת ab.call כפי שהגשר מחזיר לבלוק — שגיאה בקודי 16 §1.2. */
export type AppCallResult = { ok: true; result: unknown; meta?: AppCallMeta } | { ok: false; error: { code: string; textHe: string; consentId?: string } };
/** app@2 (apps-app2-cached-calls): פעולה עם cache — מתי חושבה, "נכון ל-", לא עדכנית, חישוב ברקע. */
export type AppCallMeta = { asOf: string | null; computedAt: string; stale: boolean; recomputing: boolean };

/**
 * 16 §1.3: הגשר מבלוק app ל-api — מסופק ע"י האפליקציה (JWT של המשתמש; ה-iframe לעולם לא רואה token).
 * context נפרד מ-DisplayContext (ש-Screen דורס) — האפליקציה עוטפת פעם אחת בשורש, כל Screen/תצוגה מקדימה רואה אותו.
 */
export type AppCallBridge = {
  call(runId: string, action: string, args: unknown, signal: AbortSignal, o?: { fresh?: boolean }): Promise<AppCallResult>;
  /** app@2: רק מהמטמון של הצופה (POST …/calls/:action/cached) — בלי סשן; אין → null. */
  peek?(runId: string, action: string, args: unknown): Promise<{ result: unknown; meta: AppCallMeta } | null>;
  /** חימום הסשן כשהבלוק נטען (POST /api/runs/:runId/warm). */
  warm?(runId: string): void;
  /** פתיחת הקונסנט בפאנל (כרטיס "ממתין לאישור"). */
  openConsent?(consentId: string): void;
  /**
   * בלוק app עם source.runId בלי html/js (display.preview של האפליקציה שריצה בנתה — באג 0d66fcc8): פלט הריצה
   * (GET /api/runs/:runId בהרשאת הצופה). null = לא נמצאה / לא הסתיימה / אין גישה.
   */
  runOutput?(runId: string): Promise<unknown>;
};
export const AppCallContext = createContext<AppCallBridge | null>(null);
